/**
 * AdminCurriculumImport — paste-and-import admin page.
 *
 * Owner pastes a JSON spec, sees parse + structural validation + a
 * counts preview, then clicks Import. Idempotent backend (matches by
 * slug/title) so re-importing safe.
 *
 * Spec shape:
 * {
 *   "sections": [
 *     {
 *       "slug": "starter-skills",
 *       "title": "Starter Skills",
 *       "target_roles": ["learner"],
 *       "order": 1,
 *       "modules": [
 *         {"title": "Cockpit Drill", "order": 1, "lessons": [
 *           {"title": "POM", "body": "...markdown...", "video_url": null, "order": 1}
 *         ]}
 *       ]
 *     }
 *   ]
 * }
 *
 * Route: /admin/curriculum
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Upload, FileJson, CheckCircle2, XCircle, Loader2,
  RefreshCw, Library, AlertTriangle, Sparkles,
} from "lucide-react";
import useSafeBack from "../hooks/useSafeBack";

const API = process.env.REACT_APP_BACKEND_URL;

const SAMPLE = {
  sections: [
    {
      slug: "starter-skills",
      title: "Starter Skills",
      target_roles: ["learner"],
      order: 1,
      modules: [
        {
          title: "Cockpit Drill",
          order: 1,
          lessons: [
            { title: "POM (Prepare-Observe-Manoeuvre)", body: "Step-by-step…", order: 1 },
            { title: "Mirror routine", body: "MSM explained…", order: 2 },
          ],
        },
      ],
    },
  ],
};


function validateSpec(spec) {
  const errors = [];
  if (!spec || typeof spec !== "object") return ["Root must be an object"];
  if (!Array.isArray(spec.sections)) return ["Missing top-level 'sections' array"];
  if (spec.sections.length === 0) return ["At least one section required"];

  const slugs = new Set();
  spec.sections.forEach((s, si) => {
    const path = `sections[${si}]`;
    if (!s.slug || typeof s.slug !== "string" || s.slug.length < 2 || s.slug.length > 80) {
      errors.push(`${path}.slug — must be 2–80 chars`);
    } else if (slugs.has(s.slug)) {
      errors.push(`${path}.slug — duplicate "${s.slug}"`);
    } else {
      slugs.add(s.slug);
    }
    if (!s.title || typeof s.title !== "string" || s.title.length < 2 || s.title.length > 120) {
      errors.push(`${path}.title — must be 2–120 chars`);
    }
    if (s.target_roles && !Array.isArray(s.target_roles)) errors.push(`${path}.target_roles — must be array`);
    if (!Array.isArray(s.modules)) {
      errors.push(`${path}.modules — must be array`);
      return;
    }
    const moduleTitles = new Set();
    s.modules.forEach((m, mi) => {
      const mp = `${path}.modules[${mi}]`;
      if (!m.title || typeof m.title !== "string") {
        errors.push(`${mp}.title — required string`);
      } else if (moduleTitles.has(m.title)) {
        errors.push(`${mp}.title — duplicate within section: "${m.title}"`);
      } else {
        moduleTitles.add(m.title);
      }
      if (m.lessons && !Array.isArray(m.lessons)) {
        errors.push(`${mp}.lessons — must be array`);
        return;
      }
      const lessonTitles = new Set();
      (m.lessons || []).forEach((l, li) => {
        const lp = `${mp}.lessons[${li}]`;
        if (!l.title || typeof l.title !== "string" || l.title.length < 2 || l.title.length > 160) {
          errors.push(`${lp}.title — required, 2–160 chars`);
        } else if (lessonTitles.has(l.title)) {
          errors.push(`${lp}.title — duplicate within module: "${l.title}"`);
        } else {
          lessonTitles.add(l.title);
        }
        if (l.video_url && typeof l.video_url !== "string") errors.push(`${lp}.video_url — must be string`);
        if (l.body && typeof l.body !== "string") errors.push(`${lp}.body — must be string`);
      });
    });
  });
  return errors;
}

function countSpec(spec) {
  const counts = { sections: 0, modules: 0, lessons: 0 };
  if (!spec?.sections) return counts;
  counts.sections = spec.sections.length;
  spec.sections.forEach((s) => {
    counts.modules += (s.modules || []).length;
    (s.modules || []).forEach((m) => {
      counts.lessons += (m.lessons || []).length;
    });
  });
  return counts;
}


export default function AdminCurriculumImport() {
  useSafeBack();
  const [text, setText] = useState("");
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  async function loadSummary() {
    try {
      const { data } = await axios.get(`${API}/api/admin/curriculum/summary`, { withCredentials: true });
      setSummary(data);
    } catch (e) {
      console.error(e);
    }
  }
  useEffect(() => { loadSummary(); }, []);

  const parsed = useMemo(() => {
    if (!text.trim()) return { spec: null, parseError: null };
    try {
      return { spec: JSON.parse(text), parseError: null };
    } catch (e) {
      return { spec: null, parseError: e.message };
    }
  }, [text]);

  const errors = useMemo(() => parsed.spec ? validateSpec(parsed.spec) : [], [parsed.spec]);
  const counts = useMemo(() => parsed.spec ? countSpec(parsed.spec) : null, [parsed.spec]);
  const ready = parsed.spec && errors.length === 0 && counts && counts.sections > 0;

  async function doImport() {
    if (!ready) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/curriculum/import`,
        parsed.spec,
        { withCredentials: true }
      );
      setResult(data);
      toast.success(
        `Import complete: +${data.inserted.sections}/${data.inserted.modules}/${data.inserted.lessons} new`,
        { description: `${data.updated.sections}/${data.updated.modules}/${data.updated.lessons} existing rows updated` }
      );
      await loadSummary();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error("Import failed", { description: detail });
      setResult({ error: detail });
    } finally {
      setBusy(false);
    }
  }

  function loadSample() {
    setText(JSON.stringify(SAMPLE, null, 2));
    setResult(null);
  }

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
          <Link
            to="/admin"
            data-testid="back-to-admin-link"
            className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 min-h-[40px] px-2"
          >
            <ArrowLeft className="w-4 h-4" /> Admin
          </Link>
          <div className="flex items-center gap-2">
            <Library className="w-5 h-5 text-emerald-600" />
            <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight">Curriculum Import</h1>
          </div>
        </div>

        {/* Current state */}
        <div data-testid="curriculum-summary" className="rounded-2xl ring-1 ring-slate-200 bg-white p-4 mb-4 grid grid-cols-3 gap-3">
          <Stat label="Sections" value={summary?.sections ?? "—"} testid="summary-sections" />
          <Stat label="Modules"  value={summary?.modules  ?? "—"} testid="summary-modules" />
          <Stat label="Lessons"  value={summary?.lessons  ?? "—"} testid="summary-lessons" />
          <button
            data-testid="refresh-summary"
            onClick={loadSummary}
            className="col-span-3 inline-flex items-center justify-center gap-1.5 min-h-[36px] text-xs font-bold text-slate-600 hover:text-slate-900"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>

        {/* Editor */}
        <div className="rounded-2xl ring-1 ring-slate-200 bg-white p-4 mb-4">
          <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
            <h2 className="text-sm font-bold text-slate-800 inline-flex items-center gap-2">
              <FileJson className="w-4 h-4" /> Paste curriculum JSON
            </h2>
            <button
              data-testid="load-sample"
              onClick={loadSample}
              className="text-xs font-bold text-indigo-600 hover:text-indigo-800 min-h-[36px] px-2"
            >
              Load sample
            </button>
          </div>
          <textarea
            data-testid="curriculum-textarea"
            value={text}
            onChange={(e) => { setText(e.target.value); setResult(null); }}
            placeholder='{"sections":[{"slug":"…","title":"…","target_roles":["learner"],"modules":[{"title":"…","lessons":[{"title":"…","body":"…"}]}]}]}'
            spellCheck={false}
            className="w-full min-h-[260px] font-mono text-xs rounded-md border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
          <p className="mt-2 text-[11px] text-slate-500">
            Idempotent. Sections matched by <code className="text-[11px]">slug</code>; modules + lessons matched by <code className="text-[11px]">title</code> within parent. Re-running the same JSON updates rather than duplicates.
          </p>
        </div>

        {/* Validation panel */}
        {text && (
          <div data-testid="validation-panel" className="rounded-2xl ring-1 ring-slate-200 bg-white p-4 mb-4">
            {parsed.parseError ? (
              <Row testid="parse-error" tone="bad" icon={XCircle}>
                JSON parse error: {parsed.parseError}
              </Row>
            ) : (
              <>
                <Row testid="parse-ok" tone="good" icon={CheckCircle2}>JSON parses cleanly</Row>
                {counts && (
                  <Row testid="counts-preview" tone="info" icon={Sparkles}>
                    {counts.sections} section{counts.sections === 1 ? "" : "s"} · {counts.modules} module{counts.modules === 1 ? "" : "s"} · {counts.lessons} lesson{counts.lessons === 1 ? "" : "s"}
                  </Row>
                )}
                {errors.length === 0 && counts.sections > 0 && (
                  <Row testid="validation-ok" tone="good" icon={CheckCircle2}>All structural checks pass</Row>
                )}
                {errors.map((err, i) => (
                  <Row key={i} testid={`validation-err-${i}`} tone="bad" icon={AlertTriangle}>{err}</Row>
                ))}
              </>
            )}
          </div>
        )}

        {/* Import button */}
        <button
          data-testid="import-button"
          onClick={doImport}
          disabled={!ready || busy}
          className={`w-full inline-flex items-center justify-center gap-2 min-h-[52px] rounded-xl text-sm font-bold transition ${
            ready
              ? "bg-emerald-600 text-white hover:bg-emerald-700"
              : "bg-slate-200 text-slate-500 cursor-not-allowed"
          }`}
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {ready
            ? `Import ${counts.sections} section · ${counts.modules} module · ${counts.lessons} lesson row${counts.lessons === 1 ? "" : "s"}`
            : "Paste valid curriculum JSON to enable"}
        </button>

        {/* Result panel */}
        {result && (
          <div data-testid="result-panel" className="mt-4 rounded-2xl ring-1 ring-slate-200 bg-white p-4">
            {result.error ? (
              <Row tone="bad" icon={XCircle} testid="result-error">{result.error}</Row>
            ) : (
              <>
                <Row tone="good" icon={CheckCircle2} testid="result-ok">Import succeeded</Row>
                <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                  <ResultStat testid="result-inserted" label="New rows inserted">
                    {result.inserted.sections} sections · {result.inserted.modules} modules · {result.inserted.lessons} lessons
                  </ResultStat>
                  <ResultStat testid="result-updated" label="Existing rows updated">
                    {result.updated.sections} sections · {result.updated.modules} modules · {result.updated.lessons} lessons
                  </ResultStat>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


function Stat({ label, value, testid }) {
  return (
    <div className="text-center">
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</div>
      <div data-testid={testid} className="text-2xl font-black text-slate-900 tabular-nums">{value}</div>
    </div>
  );
}

function ResultStat({ label, children, testid }) {
  return (
    <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 p-2.5">
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</div>
      <div data-testid={testid} className="text-xs font-bold text-slate-900 mt-0.5">{children}</div>
    </div>
  );
}

function Row({ tone, icon: Icon, children, testid }) {
  const cls =
    tone === "good" ? "text-emerald-700 bg-emerald-50" :
    tone === "bad"  ? "text-rose-700 bg-rose-50"        :
                      "text-indigo-700 bg-indigo-50";
  return (
    <div data-testid={testid} className={`flex items-start gap-2 text-xs font-semibold rounded-md px-2 py-1.5 mb-1 ${cls}`}>
      <Icon className="w-4 h-4 flex-none mt-0.5" />
      <span className="flex-1 min-w-0 break-words">{children}</span>
    </div>
  );
}
